import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyAiBenchmark,
  TechnologyAiBenchmarkUpsertPayload,
  TechnologyAiPrompt,
  TechnologyAiUsage,
  TechnologyCatalogItem,
  createTechnologyAiBenchmark,
  deleteTechnologyAiBenchmark,
  listTechnologyAiBenchmarks,
  listTechnologyAiPrompts,
  listTechnologyAiUsages,
  listTechnologyCatalogItems,
  updateTechnologyAiBenchmark,
} from '../../services/technologyApi';

const PAGE_SIZE = 10;

function todayInput(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const emptyForm: TechnologyAiBenchmarkUpsertPayload = {
  name: '',
  usageId: '',
  promptId: '',
  catalogItemId: '',
  datasetName: '',
  qualityScore: null,
  latencyMs: null,
  estimatedCost: null,
  result: '',
  recommendation: '',
  evaluatedAtUtc: todayInput(),
};

function readProblemDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: { detail?: string } } }).response;
  return response?.data?.detail ?? null;
}

function optionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function TechnologyAiBenchmarksPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.ai-benchmarks');
  const [items, setItems] = useState<TechnologyAiBenchmark[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyAiBenchmarkUpsertPayload>(emptyForm);
  const [qualityText, setQualityText] = useState('');
  const [latencyText, setLatencyText] = useState('');
  const [costText, setCostText] = useState('');
  const [saving, setSaving] = useState(false);
  const [usages, setUsages] = useState<TechnologyAiUsage[]>([]);
  const [prompts, setPrompts] = useState<TechnologyAiPrompt[]>([]);
  const [models, setModels] = useState<TechnologyCatalogItem[]>([]);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const promptsForUsage = prompts.filter((prompt) => prompt.usageId === form.usageId);

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyAiBenchmarks({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao carregar benchmarks.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    void listTechnologyAiUsages({ page: 1, pageSize: 200 })
      .then((result) => setUsages(result.items))
      .catch(() => setUsages([]));
    void listTechnologyAiPrompts({ page: 1, pageSize: 200 })
      .then((result) => setPrompts(result.items))
      .catch(() => setPrompts([]));
    void listTechnologyCatalogItems({ page: 1, pageSize: 200, category: 'AiModel' })
      .then((result) => setModels(result.items))
      .catch(() => setModels([]));
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setQualityText('');
    setLatencyText('');
    setCostText('');
    setForm({ ...emptyForm, evaluatedAtUtc: todayInput() });
  }

  function startEdit(item: TechnologyAiBenchmark): void {
    expandForm();
    setEditingId(item.id);
    setQualityText(item.qualityScore == null ? '' : String(item.qualityScore));
    setLatencyText(item.latencyMs == null ? '' : String(item.latencyMs));
    setCostText(item.estimatedCost == null ? '' : String(item.estimatedCost));
    setForm({
      name: item.name,
      usageId: item.usageId,
      promptId: item.promptId ?? '',
      catalogItemId: item.catalogItemId ?? '',
      datasetName: item.datasetName,
      qualityScore: item.qualityScore ?? null,
      latencyMs: item.latencyMs ?? null,
      estimatedCost: item.estimatedCost ?? null,
      result: item.result,
      recommendation: item.recommendation ?? '',
      evaluatedAtUtc: item.evaluatedAtUtc.slice(0, 10),
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyAiBenchmarkUpsertPayload = {
      ...form,
      promptId: form.promptId || null,
      catalogItemId: form.catalogItemId || null,
      qualityScore: optionalNumber(qualityText),
      latencyMs: optionalNumber(latencyText),
      estimatedCost: optionalNumber(costText),
      recommendation: form.recommendation?.trim() || null,
      evaluatedAtUtc: form.evaluatedAtUtc ? `${form.evaluatedAtUtc.slice(0, 10)}T12:00:00.000Z` : '',
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyAiBenchmark(editingId, payload);
        toast.success('Benchmark atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologyAiBenchmark(payload);
        toast.success('Benchmark criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao salvar benchmark.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyAiBenchmark): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir benchmark',
      message: `Deseja excluir "${item.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyAiBenchmark(item.id);
      toast.success('Benchmark excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao excluir benchmark.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Benchmarks"
        description="Avaliações de qualidade, latência e custo de um uso de IA, com o prompt e o modelo testados."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar benchmark' : 'Novo benchmark'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingId ? `Editando ${form.name || 'avaliação'}` : undefined}
          createSummary="Clique para expandir e registrar uma avaliação"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Cenário
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Uso de IA
                <select
                  className="erp-input"
                  value={form.usageId}
                  onChange={(event) =>
                    setForm({ ...form, usageId: event.target.value, promptId: '' })
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {usages.map((usage) => (
                    <option key={usage.id} value={usage.id}>
                      {usage.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Prompt
                <select
                  className="erp-input"
                  value={form.promptId ?? ''}
                  onChange={(event) => setForm({ ...form, promptId: event.target.value })}
                  disabled={!form.usageId}
                >
                  <option value="">Nenhum</option>
                  {promptsForUsage.map((prompt) => (
                    <option key={prompt.id} value={prompt.id}>
                      {prompt.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Modelo
                <select
                  className="erp-input"
                  value={form.catalogItemId ?? ''}
                  onChange={(event) => setForm({ ...form, catalogItemId: event.target.value })}
                >
                  <option value="">Nenhum</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Dataset
                <input
                  className="erp-input"
                  value={form.datasetName}
                  onChange={(event) => setForm({ ...form, datasetName: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Data da avaliação
                <input
                  type="date"
                  className="erp-input"
                  value={form.evaluatedAtUtc.slice(0, 10)}
                  onChange={(event) => setForm({ ...form, evaluatedAtUtc: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Qualidade (0 a 100)
                <input
                  className="erp-input"
                  inputMode="decimal"
                  value={qualityText}
                  onChange={(event) => setQualityText(event.target.value)}
                />
              </label>
              <label className="erp-label">
                Latência (ms)
                <input
                  className="erp-input"
                  inputMode="numeric"
                  value={latencyText}
                  onChange={(event) => setLatencyText(event.target.value)}
                />
              </label>
              <label className="erp-label">
                Custo estimado
                <input
                  className="erp-input"
                  inputMode="decimal"
                  value={costText}
                  onChange={(event) => setCostText(event.target.value)}
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Resultado
              <textarea
                className="erp-input mt-1 min-h-[80px]"
                value={form.result}
                onChange={(event) => setForm({ ...form, result: event.target.value })}
                required
              />
            </label>
            <label className="erp-label mt-3 block">
              Recomendação
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.recommendation ?? ''}
                onChange={(event) => setForm({ ...form, recommendation: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por cenário, dataset ou uso"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Cenário</th>
                  <th>Uso</th>
                  <th>Dataset</th>
                  <th>Qualidade</th>
                  <th>Latência</th>
                  <th>Data</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 7 : 6} className="text-center text-secondary-500">
                      Nenhum benchmark cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.usageName || '—'}</td>
                      <td>{item.datasetName}</td>
                      <td>{item.qualityScore ?? '—'}</td>
                      <td>{item.latencyMs == null ? '—' : `${item.latencyMs} ms`}</td>
                      <td>{item.evaluatedAtUtc.slice(0, 10)}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
